import type { ImageLoaderProps } from "next/dist/shared/lib/image-config";
import { IPageLink } from "@/sanity/globals/CTA";
import { IPopup } from "@/sanity/globals/Popups";
import { PortableTextBlock } from "sanity";
import { II18nText, II18nRichtext } from "@/sanity/globals/I18n";


export interface GROQImage {
  src: string;
  width: number;
  height: number;
  lqip: string;
  name: string;
}

export interface GROQCTA {
  name: string;
  title?: string;
  i18n?: II18nText;
  icon?: string;
  action: "link" | "popup" | "anchor";
  link?: IPageLink;
  popup?: IPopup;
  anchor?: string;
}

export interface GROQButton {
  type?:
    | "default"
    | "white"
    | "outline"
    | "ghost"
    | "outline-black"
    | "green"
    | "discord";
  size?: "sm" | "default" | "lg";
  shape?: "square" | "round" | "pill";
  cta?: GROQCTA;
}

export interface GROQCornerButton {
  type?: "default" | "dark";
  cta?: GROQCTA;
}

export interface IHeaderColumn {
  _key?: string;
  columnTitle?: string;
  columnTitleI18n?: II18nText;
  columnType: "subItems" | "imageSubItems" | "fullImage" | "none";
  subItems?: {
    title: string;
    i18n?: II18nText;
    description?: string;
    descriptionI18n?: II18nText;
    icon?: GROQImage;
    link: IPageLink;
  }[];
  imageSubItems?: {
    image: GROQImage;
    imageI18n?: { de?: GROQImage; br?: GROQImage };
    title: string;
    i18n?: II18nText;
    description?: string;
    descriptionI18n?: II18nText;
    moreInfo?: string;
    moreInfoI18n?: II18nText;
    link: IPageLink;
  }[];
  fullImage?: {
    image?: GROQImage;
    imageI18n?: { de?: GROQImage; br?: GROQImage };
    mobileImage?: GROQImage;
    mobileImageI18n?: { de?: GROQImage; br?: GROQImage };
    link?: IPageLink;
  };
}

export interface IHeader {
  name: string;
  menu: {
    title: string;
    i18n?: II18nText;
    link?: IPageLink;
    isCategory: boolean;
    columns?: IHeaderColumn[];
  }[];
  headerDiscordCTA: GROQButton;
  headerLoginCTA: GROQButton;
  headerCTA: GROQButton;
}

export interface IFinalCta {
  finalCtaChip?: string;
  finalCtaTitle?: PortableTextBlock[];
  finalCtaButton?: GROQButton;
  finalCtaImage?: GROQImage;
}

export interface IFooter extends IFinalCta {
  finalCtaChipI18n?: II18nText;
  finalCtaTitleI18n?: II18nRichtext;
  navColumns: {
    heading: string;
    i18n?: II18nText;
    links: {
      title: string;
      i18n?: II18nText;
      link: IPageLink;
    }[];
  }[];
  askAiLabel?: string;
  askAiLabelI18n?: II18nText;
  askAiPrompt?: string;
  askAiPromptI18n?: II18nText;
  legalEntity?: string;
  disclaimer?: PortableTextBlock[];
  disclaimerI18n?: II18nRichtext;
}

export interface ITrustpilotTestimonial {
  name: string;
  testimonials: {
    image: GROQImage;
    name: string;
    position: string;
    text: PortableTextBlock[];
  }[];
}

export interface ITraders {
  name: string;
  items: {
    image: GROQImage;
    text: PortableTextBlock[];
  }[];
}

export interface ITeamMemberSocialLink {
  _key: string;
  platform: 'linkedin' | 'youtube' | 'instagram' | 'website' | 'twitter';
  url: string;
}

export interface ITeamMember {
  _id?: string;
  name: string;
  role: string;
  roleI18n?: II18nText;
  quote?: PortableTextBlock[];
  i18n?: II18nRichtext;
  image?: GROQImage;
  youtubeSubscribers?: string;
  youtubeSubscribersI18n?: II18nText;
  socialLinks?: ITeamMemberSocialLink[];
}

export interface GROQMuxVideo {
  playbackId: string;
  assetId: string;
  filename: string;
  status: string;
}

export function GROQImageLoader({
  src,
  width,
  quality = 90,
}: ImageLoaderProps): string {
  return `${src}?auto=format&q=${quality}&w=${width}`;
}

export function GROQUnoptimizedImageLoader({
  src,
  width,
  quality = 75,
}: ImageLoaderProps): string {
  return src + "?q=100";
}

export const GROQImageSchema =
  '{"src": asset->url, "width": asset->metadata.dimensions.width, "height": asset->metadata.dimensions.height, "lqip": asset->metadata.lqip, "name": asset->originalFilename}';

export const GROQPageLinkSchema =
  "{link,externalLink,openInNewTab,url,anchor,internalLink->{settings{slug{current}}}}";

export const HubSpotFormSchema = `
{
  _id,
  name,
  fields,
  settings{
    onSubmit,
    submitText,
    message,
    redirect${GROQPageLinkSchema},
    portalId,
    formId,
  }
}
`;

export const ZapierFormSchema = `
{
  _id,
  name,
  fields,
  settings{
    onSubmit,
    submitText,
    message,
    redirect${GROQPageLinkSchema},
    url,
  }
}
`;

export const GROQPopupSchemaQuery = `{
  name,
  theme,
  chip,
  content,
  formType,
  hsForm->${HubSpotFormSchema},
  zapierForm->${ZapierFormSchema},
}`;

export const GROQFormSchema = `{
  _id,
  name,
  fields,
  settings{
    onSubmit,
    submitText,
    message,
    redirect${GROQPageLinkSchema},
    portalId,
    formId,
  }
}`;

export const GROQPageCTASchema = `{
  name,
  title,
  i18n,
  icon,
  action,
  anchor,
  link${GROQPageLinkSchema},
  popup->${GROQPopupSchemaQuery}
}`;

export const GROQButtonSchema = `{
  type,
  size,
  shape,
  cta->${GROQPageCTASchema}
}`;

export const GROQCornerButtonSchema = `{
  type,
  cta->${GROQPageCTASchema}
}`;

export const GROQMuxVideoShema = `{
  "playbackId": asset->playbackId,
  "assetId": asset->assetId,
  "filename": asset->filename,
  "status": asset->status
}`;

export const MuxVideoPlayerSchema = `{
  embed,
  muxVideo${GROQMuxVideoShema},
  embedUrl,
  thumbnail${GROQImageSchema}
}`;

export const HeaderSchema = `->{
  name,
  menu[hidden != true]{
    title,
    i18n,
    link${GROQPageLinkSchema},
    isCategory,
    columns[]{
      _key,
      columnTitle,
      columnTitleI18n,
      columnType,
      subItems[hidden != true]{
        title,
        i18n,
        description,
        descriptionI18n,
        icon${GROQImageSchema},
        link${GROQPageLinkSchema},
      },
      imageSubItems[hidden != true]{
        image${GROQImageSchema},
        imageI18n{ de${GROQImageSchema}, br${GROQImageSchema} },
        title,
        i18n,
        description,
        descriptionI18n,
        moreInfo,
        moreInfoI18n,
        link${GROQPageLinkSchema},
      },
      fullImage{
        image${GROQImageSchema},
        imageI18n{ de${GROQImageSchema}, br${GROQImageSchema} },
        mobileImage${GROQImageSchema},
        mobileImageI18n{ de${GROQImageSchema}, br${GROQImageSchema} },
        link${GROQPageLinkSchema},
      },
    },
  },
  headerDiscordCTA${GROQButtonSchema},
  headerLoginCTA${GROQButtonSchema},
  headerCTA${GROQButtonSchema},
}`;

export const FooterSchema = `->{
  finalCtaChip,
  finalCtaChipI18n,
  finalCtaTitle,
  finalCtaTitleI18n,
  finalCtaButton${GROQButtonSchema},
  finalCtaImage${GROQImageSchema},
  navColumns[]{
    heading,
    i18n,
    links[]{
      title,
      i18n,
      link${GROQPageLinkSchema},
    },
  },
  askAiLabel,
  askAiLabelI18n,
  askAiPrompt,
  askAiPromptI18n,
  legalEntity,
  disclaimer,
  disclaimerI18n,
}`;

export const TrustpilotTestimonialsSchema = `{
  name,
  testimonials[]{
    image${GROQImageSchema},
    name,
    position,
    text,
  },
}`;

export const TradersSchema = `{
  name,
  items[]{
    image${GROQImageSchema},
    text,
  },
}`;

export const TeamMemberSchema = `{
  _id,
  name,
  role,
  roleI18n,
  quote,
  i18n,
  image${GROQImageSchema},
  youtubeSubscribers,
  youtubeSubscribersI18n,
  socialLinks[]{ _key, platform, url },
}`;

export interface IPayoutCertificate {
  _id?: string;
  name?: string;
  image?: GROQImage;
}

export const PayoutCertificateSchema = `{
  _id,
  name,
  image${GROQImageSchema},
}`;

export interface IPricingCardFeature {
  _key?: string;
  label?: string;
  labelI18n?: II18nText;
  value?: string;
  valueI18n?: II18nText;
  description?: string;
  descriptionI18n?: II18nText;
}

export interface IPricingCard {
  _id?: string;
  name?: string;
  highlight?: boolean;
  theme?: 'light' | 'sage' | 'dark';
  badge?: string;
  badgeI18n?: II18nText;
  amount?: string;
  feeAmount?: string;
  feeSuffix?: string;
  feeSuffixI18n?: II18nText;
  assetType?: 'futures' | 'cfd_crypto';
  accountType?: 'challenge' | 'instant_funding';
  headerImage?: GROQImage;
  cta?: GROQButton;
  features?: IPricingCardFeature[];
  fundedFeatures?: IPricingCardFeature[];
  fundedFeeAmount?: string;
  fundedFeeSuffix?: string;
  fundedFeeSuffixI18n?: II18nText;
}

export const PricingCardSchema = `{
  _id,
  name,
  highlight,
  theme,
  badge,
  badgeI18n,
  amount,
  feeAmount,
  feeSuffix,
  feeSuffixI18n,
  assetType,
  accountType,
  headerImage${GROQImageSchema},
  cta${GROQButtonSchema},
  features[]{ _key, label, labelI18n, value, valueI18n, description, descriptionI18n },
  fundedFeatures[]{ _key, label, labelI18n, value, valueI18n, description, descriptionI18n },
  fundedFeeAmount,
  fundedFeeSuffix,
  fundedFeeSuffixI18n,
}`;

export interface IPricingCardSlot extends IPricingCard {
  discountOverride?: number;
}

export const PricingCardSlotSchema = `{
  "_id": card->._id,
  "name": card->.name,
  "highlight": card->.highlight,
  "theme": card->.theme,
  "badge": card->.badge,
  "badgeI18n": card->.badgeI18n,
  "amount": card->.amount,
  "feeAmount": card->.feeAmount,
  "feeSuffix": card->.feeSuffix,
  "feeSuffixI18n": card->.feeSuffixI18n,
  "assetType": card->.assetType,
  "accountType": card->.accountType,
  "headerImage": card->headerImage${GROQImageSchema},
  "cta": select(defined(ctaOverride) => ctaOverride${GROQButtonSchema}, card->cta${GROQButtonSchema}),
  "features": card->features[]{ _key, label, labelI18n, value, valueI18n, description, descriptionI18n },
  "fundedFeatures": card->fundedFeatures[]{ _key, label, labelI18n, value, valueI18n, description, descriptionI18n },
  "fundedFeeAmount": card->.fundedFeeAmount,
  "fundedFeeSuffix": card->.fundedFeeSuffix,
  "fundedFeeSuffixI18n": card->.fundedFeeSuffixI18n,
  discountOverride,
}`;

// ── Pricing Section V2 ────────────────────────────────────────────────────────

export interface IPricingSpecDefinition {
  _key?: string;
  key?: string;
  label?: string;
  labelI18n?: II18nText;
  tooltip?: string;
  tooltipI18n?: II18nText;
}

export interface IPricingTierSpec {
  _key?: string;
  key?: string;
  value?: string;
  valueI18n?: II18nText;
  /**
   * Specs-toggle overrides. The two tabs are different products, so a row can
   * carry its own number on the secondary tab or exist on one tab only.
   * Undefined `showIn` means "both" — every stored row predates these fields.
   */
  showIn?: 'both' | 'primary' | 'secondary';
  secondaryValue?: string;
  secondaryValueI18n?: II18nText;
}

export interface IPricingTierFee {
  current?: string;
  currentI18n?: II18nText;
  original?: string;
  originalI18n?: II18nText;
  discountLabel?: string;
  discountLabelI18n?: II18nText;
  suffix?: string;
  suffixI18n?: II18nText;
}

export interface IPricingTierCTA {
  title?: string;
  titleI18n?: II18nText;
  href?: string;
  variant?: 'dark' | 'green' | 'outline';
}

export interface IPricingTierSpecsToggle {
  primaryLabel?: string;
  primaryLabelI18n?: II18nText;
  primaryRowKey?: string;
  secondaryLabel?: string;
  secondaryLabelI18n?: II18nText;
  secondaryRowKey?: string;
  secondaryValue?: string;
  secondaryValueI18n?: II18nText;
}

export interface IPricingTier {
  _key?: string;
  key?: string;
  label?: string;
  labelI18n?: II18nText;
  amount?: string;
  amountI18n?: II18nText;
  theme?: 'light-green' | 'green' | 'dark';
  hasSpecsToggle?: boolean;
  specsToggle?: IPricingTierSpecsToggle;
  specs?: IPricingTierSpec[];
  fee?: IPricingTierFee;
  secondaryFee?: IPricingTierFee;
  ctaPrimary?: IPricingTierCTA;
  secondaryCtaPrimary?: IPricingTierCTA;
  fundedSubLabel?: string;
  fundedSubLabelI18n?: II18nText;
  fundedSpecs?: IPricingTierSpec[];
  fundedCtaPrimary?: IPricingTierCTA;
  flipToFundedLabel?: string;
  flipToFundedLabelI18n?: II18nText;
  flipBackLabel?: string;
  flipBackLabelI18n?: II18nText;
}

export interface IPricingCategory {
  _key?: string;
  key?: string;
  name?: string;
  nameI18n?: II18nText;
  chip?: string;
  chipI18n?: II18nText;
  isNew?: boolean;
  description?: string;
  descriptionI18n?: II18nText;
  /** Longer explanation shown in the ⓘ tooltip on the category selector card. */
  tooltip?: string;
  tooltipI18n?: II18nText;
  subLabel?: string;
  subLabelI18n?: II18nText;
  tiers?: IPricingTier[];
}

export interface IPricingAsset {
  _key?: string;
  key?: string;
  label?: string;
  labelI18n?: II18nText;
  platformLogosGroupKey?: string;
  categories?: IPricingCategory[];
}

export interface IPricingComparison {
  content?: PortableTextBlock[];
  contentI18n?: II18nRichtext;
  showLabel?: string;
  showLabelI18n?: II18nText;
  hideLabel?: string;
  hideLabelI18n?: II18nText;
  addPlanLabel?: string;
  addPlanLabelI18n?: II18nText;
  productPlaceholder?: string;
  productPlaceholderI18n?: II18nText;
  accountSizePlaceholder?: string;
  accountSizePlaceholderI18n?: II18nText;
  emptyValueLabel?: string;
  emptyValueLabelI18n?: II18nText;
  highlightHint?: string;
  highlightHintI18n?: II18nText;
  rowKeys?: string[];
}

export interface IPlatformPopupBullet {
  _key?: string;
  text?: string;
  textI18n?: II18nText;
}

export interface IPlatformItem {
  _key?: string;
  name?: string;
  nameI18n?: II18nText;
  logo?: GROQImage;
  isNew?: boolean;
  href?: string;
  popupImage?: GROQImage;
  popupBulletColor?: string;
  popupBullets?: IPlatformPopupBullet[];
  popupCtaLabel?: string;
  popupCtaLabelI18n?: II18nText;
}

export interface IPlatformsGroup {
  _key?: string;
  key?: string;
  label?: string;
  labelI18n?: II18nText;
  items?: IPlatformItem[];
}

export interface IPlatformsFooter {
  chip?: string;
  chipI18n?: II18nText;
  content?: PortableTextBlock[];
  contentI18n?: II18nRichtext;
  groups?: IPlatformsGroup[];
}

export interface IPricingSectionV2 {
  _id?: string;
  _type?: 'pricingSectionV2';
  name?: string;
  /** Studio toggle: homepage renders the new pricing experience when true. */
  useNewPricing?: boolean;
  heroChip?: string;
  heroChipI18n?: II18nText;
  heroContent?: PortableTextBlock[];
  heroContentI18n?: II18nRichtext;
  /** Shared flip button labels — override per-tier flipToFundedLabel when set. */
  flipToFundedLabel?: string;
  flipToFundedLabelI18n?: II18nText;
  flipBackLabel?: string;
  flipBackLabelI18n?: II18nText;
  specsCatalog?: IPricingSpecDefinition[];
  assets?: IPricingAsset[];
  comparison?: IPricingComparison;
  platformsFooter?: IPlatformsFooter;
}

export const PricingSectionV2Schema = `{
  _id,
  _type,
  name,
  useNewPricing,
  heroChip,
  heroChipI18n,
  heroContent,
  heroContentI18n,
  flipToFundedLabel, flipToFundedLabelI18n,
  flipBackLabel, flipBackLabelI18n,
  specsCatalog[]{ _key, key, label, labelI18n, tooltip, tooltipI18n },
  assets[]{
    _key, key, label, labelI18n, platformLogosGroupKey,
    categories[]{
      _key, key, name, nameI18n, chip, chipI18n, isNew,
      description, descriptionI18n,
      tooltip, tooltipI18n,
      subLabel, subLabelI18n,
      tiers[]{
        _key, key, label, labelI18n, amount, amountI18n, theme,
        hasSpecsToggle,
        specsToggle{
          primaryLabel, primaryLabelI18n, primaryRowKey,
          secondaryLabel, secondaryLabelI18n, secondaryRowKey,
          secondaryValue, secondaryValueI18n,
        },
        specs[]{ _key, key, value, valueI18n, showIn, secondaryValue, secondaryValueI18n },
        fee{
          current, currentI18n,
          original, originalI18n,
          discountLabel, discountLabelI18n,
          suffix, suffixI18n,
        },
        secondaryFee{
          current, currentI18n,
          original, originalI18n,
          discountLabel, discountLabelI18n,
          suffix, suffixI18n,
        },
        ctaPrimary{ title, titleI18n, href, variant },
        secondaryCtaPrimary{ title, titleI18n, href, variant },
        fundedSubLabel, fundedSubLabelI18n,
        fundedSpecs[]{ _key, key, value, valueI18n, showIn, secondaryValue, secondaryValueI18n },
        fundedCtaPrimary{ title, titleI18n, href, variant },
        flipToFundedLabel, flipToFundedLabelI18n,
        flipBackLabel, flipBackLabelI18n,
      },
    },
  },
  comparison{
    content, contentI18n,
    showLabel, showLabelI18n,
    hideLabel, hideLabelI18n,
    addPlanLabel, addPlanLabelI18n,
    productPlaceholder, productPlaceholderI18n,
    accountSizePlaceholder, accountSizePlaceholderI18n,
    emptyValueLabel, emptyValueLabelI18n,
    highlightHint, highlightHintI18n,
    rowKeys,
  },
  platformsFooter{
    chip, chipI18n,
    content, contentI18n,
    groups[]{
      _key, key, label, labelI18n,
      items[]{
        _key, name, nameI18n, logo${GROQImageSchema}, isNew, href,
        popupImage${GROQImageSchema},
        popupBulletColor,
        popupBullets[]{ _key, text, textI18n },
        popupCtaLabel, popupCtaLabelI18n,
      },
    },
  },
}`;

/* ── Pricing Section V3 — full Sanity control of the NEW pricing section ──
   Mirrors sanity/globals/PricingSectionV3.ts 1:1. Every field is optional:
   lib/new-pricing/fromSanity.ts merges the doc over the code defaults. */

export interface IV3PlatformLogo {
  _key?: string;
  image?: GROQImage;
  label?: string;
  widthRem?: number;
}

export interface IV3PlatformCard {
  _key?: string;
  key?: string;
  title?: string;
  titleI18n?: II18nText;
  badgeNew?: boolean;
  logos?: IV3PlatformLogo[];
}

export interface IV3SelectorAsset {
  _key?: string;
  key?: string;
  label?: string;
  labelI18n?: II18nText;
  defaultPlatform?: string;
  platforms?: IV3PlatformCard[];
}

export interface IV3Product {
  _key?: string;
  key?: string;
  name?: string;
  nameI18n?: II18nText;
  tag?: string;
  tagI18n?: II18nText;
  isNew?: boolean;
  tooltip?: string;
  tooltipI18n?: II18nText;
  maxAccounts?: number;
}

export interface IV3CustomSpec {
  _key?: string;
  kpiKey?: string;
  label?: string;
  labelI18n?: II18nText;
  value?: string;
  valueI18n?: II18nText;
}

export interface IV3ChallengeCol {
  _key?: string;
  variant?: string;
  presel?: string;
  platforms?: string[];
  customSpecs?: IV3CustomSpec[];
  priceUsd?: number;
  profitTargetUsd?: number;
  maxDrawdownUsd?: number;
  maxPositionLossPct?: number;
  dailyLossLimitUsd?: number;
  consistencyPct?: number;
  durationDays?: number;
  extensionFeeUsd?: number;
}

export interface IV3FundedCol {
  _key?: string;
  variant?: string;
  presel?: string;
  platforms?: string[];
  customSpecs?: IV3CustomSpec[];
  priceUsd?: number;
  activationFree?: boolean;
  activationFeeUsd?: number;
  profitSplitTrader?: number;
  payoutEveryDays?: number;
  maxDrawdownUsd?: number;
  maxPositionLossPct?: number;
  dailyLossLimitUsd?: number;
  consistencyPct?: number;
  resetFeeUsd?: number;
  extensionFeeUsd?: number;
}

export interface IV3CatalogSize {
  _key?: string;
  size?: string;
  tierName?: string;
  platforms?: string[];
  challengeCols?: IV3ChallengeCol[];
  fundedCols?: IV3FundedCol[];
}

export interface IV3CatalogProduct {
  _key?: string;
  productKey?: string;
  sizes?: IV3CatalogSize[];
}

export interface IV3CatalogAsset {
  _key?: string;
  assetKey?: string;
  products?: IV3CatalogProduct[];
}

export interface IV3KpiDefinition {
  _key?: string;
  key?: string;
  label?: string;
  labelI18n?: II18nText;
  tooltip?: string;
  tooltipI18n?: II18nText;
}

export interface IV3VariantLabel {
  _key?: string;
  key?: string;
  label?: string;
  labelI18n?: II18nText;
}

export interface IPricingSectionV3 {
  _id?: string;
  _type?: 'pricingSectionV3';
  name?: string;

  discountPercent?: number;
  checkoutBaseUrl?: string;
  rapidFundedVariants?: 'both' | 'classic' | 'daily';
  mostPopularSize?: string;

  heroEyebrow?: string;
  heroEyebrowI18n?: II18nText;
  heroTitleLine1?: string;
  heroTitleLine1I18n?: II18nText;
  heroTitleAccent?: string;
  heroTitleAccentI18n?: II18nText;
  heroSub?: string;
  heroSubI18n?: II18nText;

  selectorAssets?: IV3SelectorAsset[];
  products?: IV3Product[];
  catalogAssets?: IV3CatalogAsset[];
  kpiCatalog?: IV3KpiDefinition[];

  marketFuturesLabel?: string;
  marketFuturesLabelI18n?: II18nText;
  marketCfdCryptoLabel?: string;
  marketCfdCryptoLabelI18n?: II18nText;
  badgeNew?: string;
  badgeNewI18n?: II18nText;
  badgeMostPopular?: string;
  badgeMostPopularI18n?: II18nText;
  stageChallenge?: string;
  stageChallengeI18n?: II18nText;
  stageFunded?: string;
  stageFundedI18n?: II18nText;
  stageInstantOnly?: string;
  stageInstantOnlyI18n?: II18nText;
  keyFeaturesLabel?: string;
  keyFeaturesLabelI18n?: II18nText;
  chosenPathLabel?: string;
  chosenPathLabelI18n?: II18nText;
  variantLabels?: IV3VariantLabel[];
  valueNone?: string;
  valueNoneI18n?: II18nText;
  valueFree?: string;
  valueFreeI18n?: II18nText;
  drawdownModeValue?: string;
  drawdownModeValueI18n?: II18nText;
  durationTemplate?: string;
  durationTemplateI18n?: II18nText;
  payoutDaySingular?: string;
  payoutDaySingularI18n?: II18nText;
  payoutDaysTemplate?: string;
  payoutDaysTemplateI18n?: II18nText;
  priceAccountLabel?: string;
  priceAccountLabelI18n?: II18nText;
  priceOneTimeFeeLabel?: string;
  priceOneTimeFeeLabelI18n?: II18nText;
  ctaGetPlan?: string;
  ctaGetPlanI18n?: II18nText;
  ctaViewVariantTemplate?: string;
  ctaViewVariantTemplateI18n?: II18nText;
  unsureTitle?: string;
  unsureTitleI18n?: II18nText;
  unsureSub?: string;
  unsureSubI18n?: II18nText;
  unsureHelpMeChoose?: string;
  unsureHelpMeChooseI18n?: II18nText;
  unsureCompare?: string;
  unsureCompareI18n?: II18nText;
  footnoteMpl?: string;
  footnoteMplI18n?: II18nText;
  footnoteDll?: string;
  footnoteDllI18n?: II18nText;

  compareTitle?: string;
  compareTitleI18n?: II18nText;
  compareSub?: string;
  compareSubI18n?: II18nText;
  compareLegendMpl?: string;
  compareLegendMplI18n?: II18nText;
  compareLegendDll?: string;
  compareLegendDllI18n?: II18nText;
  compareOneTimeFee?: string;
  compareOneTimeFeeI18n?: II18nText;
  compareChallenge?: string;
  compareChallengeI18n?: II18nText;
  compareEmptySlot?: string;
  compareEmptySlotI18n?: II18nText;
  compareMobileHint?: string;
  compareMobileHintI18n?: II18nText;

  platformsFooter?: IPlatformsFooter;
}

export const PricingSectionV3Schema = `{
  _id,
  _type,
  name,
  discountPercent,
  checkoutBaseUrl,
  rapidFundedVariants,
  mostPopularSize,
  heroEyebrow, heroEyebrowI18n,
  heroTitleLine1, heroTitleLine1I18n,
  heroTitleAccent, heroTitleAccentI18n,
  heroSub, heroSubI18n,
  selectorAssets[]{
    _key, key, label, labelI18n, defaultPlatform,
    platforms[]{
      _key, key, title, titleI18n, badgeNew,
      logos[]{ _key, image${GROQImageSchema}, label, widthRem },
    },
  },
  products[]{
    _key, key, name, nameI18n, tag, tagI18n, isNew, tooltip, tooltipI18n, maxAccounts,
  },
  catalogAssets[]{
    _key, assetKey,
    products[]{
      _key, productKey,
      sizes[]{
        _key, size, tierName, platforms,
        challengeCols[]{
          _key, variant, presel, platforms,
          priceUsd, profitTargetUsd, maxDrawdownUsd, maxPositionLossPct,
          dailyLossLimitUsd, consistencyPct, durationDays, extensionFeeUsd,
          customSpecs[]{ _key, kpiKey, label, labelI18n, value, valueI18n },
        },
        fundedCols[]{
          _key, variant, presel, platforms,
          priceUsd, activationFree, activationFeeUsd, profitSplitTrader,
          payoutEveryDays, maxDrawdownUsd, maxPositionLossPct, dailyLossLimitUsd,
          consistencyPct, resetFeeUsd, extensionFeeUsd,
          customSpecs[]{ _key, kpiKey, label, labelI18n, value, valueI18n },
        },
      },
    },
  },
  kpiCatalog[]{ _key, key, label, labelI18n, tooltip, tooltipI18n },
  marketFuturesLabel, marketFuturesLabelI18n,
  marketCfdCryptoLabel, marketCfdCryptoLabelI18n,
  badgeNew, badgeNewI18n,
  badgeMostPopular, badgeMostPopularI18n,
  stageChallenge, stageChallengeI18n,
  stageFunded, stageFundedI18n,
  stageInstantOnly, stageInstantOnlyI18n,
  keyFeaturesLabel, keyFeaturesLabelI18n,
  chosenPathLabel, chosenPathLabelI18n,
  variantLabels[]{ _key, key, label, labelI18n },
  valueNone, valueNoneI18n,
  valueFree, valueFreeI18n,
  drawdownModeValue, drawdownModeValueI18n,
  durationTemplate, durationTemplateI18n,
  payoutDaySingular, payoutDaySingularI18n,
  payoutDaysTemplate, payoutDaysTemplateI18n,
  priceAccountLabel, priceAccountLabelI18n,
  priceOneTimeFeeLabel, priceOneTimeFeeLabelI18n,
  ctaGetPlan, ctaGetPlanI18n,
  ctaViewVariantTemplate, ctaViewVariantTemplateI18n,
  unsureTitle, unsureTitleI18n,
  unsureSub, unsureSubI18n,
  unsureHelpMeChoose, unsureHelpMeChooseI18n,
  unsureCompare, unsureCompareI18n,
  footnoteMpl, footnoteMplI18n,
  footnoteDll, footnoteDllI18n,
  compareTitle, compareTitleI18n,
  compareSub, compareSubI18n,
  compareLegendMpl, compareLegendMplI18n,
  compareLegendDll, compareLegendDllI18n,
  compareOneTimeFee, compareOneTimeFeeI18n,
  compareChallenge, compareChallengeI18n,
  compareEmptySlot, compareEmptySlotI18n,
  compareMobileHint, compareMobileHintI18n,
  platformsFooter{
    chip, chipI18n,
    content, contentI18n,
    groups[]{
      _key, key, label, labelI18n,
      items[]{
        _key, name, nameI18n, logo${GROQImageSchema}, isNew, href,
        popupImage${GROQImageSchema},
        popupBulletColor,
        popupBullets[]{ _key, text, textI18n },
        popupCtaLabel, popupCtaLabelI18n,
      },
    },
  },
}`;

export interface ITestimonialCard {
  _id?: string;
  _key?: string;
  name?: string;
  role?: string;
  date?: string;
  verifiedLabel?: string;
  quote?: PortableTextBlock[];
  i18n?: II18nRichtext;
}

export const TestimonialCardSchema = `->cards[]{ _key, date, quote, i18n, name, role, verifiedLabel }`;

export interface IReviewTestimonial {
  _id?: string;
  authorName?: string;
  authorRole?: string;
  date?: string;
  quote?: PortableTextBlock[];
  i18n?: II18nRichtext;
}

export const ReviewTestimonialSchema = `{
  _id,
  authorName,
  authorRole,
  date,
  quote,
  i18n,
}`;

export interface ITraderValueCard {
  _id?: string;
  _key?: string;
  backgroundImage?: GROQImage;
  label?: string;
  content?: PortableTextBlock[];
}

export const TraderValueCardSchema = `{
  _id,
  backgroundImage${GROQImageSchema},
  label,
  content,
}`;

export interface IFaqItem {
  _id?: string;
  _key?: string;
  question?: string;
  questionI18n?: II18nText;
  answer?: PortableTextBlock[];
  answerI18n?: II18nRichtext;
}

export const FaqItemSchema = `{
  _id,
  question,
  questionI18n,
  answer,
  answerI18n,
}`;

export interface ITraderStoryStat {
  _key?: string;
  value?: string;
  label?: string;
  labelI18n?: II18nText;
}

export interface ITraderStory {
  _id?: string;
  _key?: string;
  title?: PortableTextBlock[];
  titleI18n?: II18nRichtext;
  traderName?: string;
  traderRole?: string;
  traderRoleI18n?: II18nText;
  traderImage?: GROQImage;
  traderImageI18n?: { de?: GROQImage; br?: GROQImage };
  stats?: ITraderStoryStat[];
  question?: string;
  questionI18n?: II18nText;
  answer?: string;
  answerI18n?: II18nText;
  hasVideo?: boolean;
  watchInterviewLabel?: string;
  watchInterviewLabelI18n?: II18nText;
  watchInterviewLink?: IPageLink;
}

export const TraderStorySchema = `{
  _id,
  _key,
  title,
  titleI18n,
  traderName,
  traderRole,
  traderRoleI18n,
  traderImage${GROQImageSchema},
  traderImageI18n{ de${GROQImageSchema}, br${GROQImageSchema} },
  stats[]{ _key, value, label, labelI18n },
  question,
  questionI18n,
  answer,
  answerI18n,
  hasVideo,
  watchInterviewLabel,
  watchInterviewLabelI18n,
  watchInterviewLink${GROQPageLinkSchema},
}`;

export interface ITraderValuesSectionData {
  traderValuesChip?: string;
  traderValuesTitle?: PortableTextBlock[];
  traderValueCards?: ITraderValueCard[];
  traderValueCardsRefs?: ITraderValueCard[];
}

/**
 * Design switch + optional countdown shared by the Top Banner and the Promo
 * Banner documents (sanity/globals/BannerDesign.ts). `design` is undefined on
 * every document saved before the switch existed — treat that as "default".
 * "halloween" and "blackMonth" are the seasonal skins of the "v1" design.
 */
export interface IBannerDesign {
  design?: 'default' | 'v1' | 'halloween' | 'blackMonth';
  showCountdown?: boolean;
  countdownStartDate?: string;
  countdownEndDate?: string;
}

export const GROQBannerDesignSchema = `
  design,
  showCountdown,
  countdownStartDate,
  countdownEndDate,`;

export interface ITopBanner extends IBannerDesign {
  name: string;
  preTextIcon?: GROQImage;
  preText?: string;
  preTextI18n?: II18nText;
  text: string;
  textI18n?: II18nText;
  /** Phone copy of the seasonal designs (Halloween / Black Month); empty = `text`. */
  mobileText?: string;
  mobileTextI18n?: II18nText;
  ctaLabel?: string;
  ctaLabelI18n?: II18nText;
  ctaLink?: IPageLink;
}

export const TopBannerSchema = `{
  name,
  ${GROQBannerDesignSchema}
  preTextIcon${GROQImageSchema},
  preText,
  preTextI18n,
  text,
  textI18n,
  mobileText,
  mobileTextI18n,
  ctaLabel,
  ctaLabelI18n,
  ctaLink${GROQPageLinkSchema},
}`;

export interface IPromoBanner extends IBannerDesign {
  name: string;
  image?: GROQImage;
  text?: PortableTextBlock[];
  textI18n?: II18nRichtext;
  button?: GROQButton;
}

export const PromoBannerSchema = `{
  name,
  ${GROQBannerDesignSchema}
  image${GROQImageSchema},
  text,
  textI18n,
  button${GROQButtonSchema},
}`;

// Global "Affiliate Discount Settings" singleton. Besides the discount
// percentages it also carries the top banner + promo popup that apply to every
// affiliate page at once — the page's own Show top/promo banner settings are
// only the fallback (see lib/affiliateBanners.ts).
export const AffiliateDiscountSettingsSchema = `{
  activateDiscountForAffiliates,
  endDate,
  futuresChallengeDiscountPercent,
  futuresInstantFundingDiscountPercent,
  cryptoChallengeDiscountPercent,
  cryptoInstantFundingDiscountPercent,
  maxDiscountPercent,
  showTopBanner,
  topBanner->${TopBannerSchema},
  showPromoBanner,
  promoBanner->${PromoBannerSchema},
}`;

// Drop-in projection for every affiliate page query.
export const GROQAffiliateDiscountSettings = `"affiliateDiscountSettings": *[_type == "affiliateDiscountSettings" && _id == "affiliateDiscountSettings"][0]${AffiliateDiscountSettingsSchema}`;

export interface IAffiliateHeroItem {
  icon?: GROQImage;
  text?: string;
  textI18n?: II18nText;
}

export const AffiliateHeroItemSchema = `{
  icon${GROQImageSchema},
  text,
  textI18n,
}`;

export interface IAffiliateHeroItems {
  items?: IAffiliateHeroItem[];
}

export const AffiliateHeroItemsSchema = `{
  items[]${AffiliateHeroItemSchema},
}`;

export const GROQAffiliateHeroItems = `"affiliateHeroItems": *[_type == "affiliateHeroItems" && _id == "affiliateHeroItems"][0]${AffiliateHeroItemsSchema}`;

export interface IAffiliateHeroRecommendation {
  icon?: GROQImage;
  text?: string;
}

export interface IAffiliateRulesItem {
  icon?: GROQImage;
  text?: string;
  textI18n?: II18nText;
}

export const AffiliateRulesItemSchema = `{
  icon${GROQImageSchema},
  text,
  textI18n,
}`;

// The global "Affiliate Global Sections" document — one per vertical, Futures
// and Crypto. Unlike the singletons above this one is picked per page through a
// reference, so it is dereferenced inside the page query rather than looked up
// by a fixed _id.
export interface IAffiliateGlobalSections {
  name?: string;
  rulesItems?: IAffiliateRulesItem[];
}

// Only the cards are shared. The chip and the heading above them stay on each
// page — those vary on purpose, the cards varied by accident.
export const AffiliateGlobalSectionsSchema = `{
  name,
  rulesItems[]${AffiliateRulesItemSchema},
}`;

// Drop-in projection for every affiliate page query that opts into a global section.
export const GROQAffiliateGlobalSections = `affiliateGlobalSectionsRef->${AffiliateGlobalSectionsSchema}`;

export interface IScrollingWords {
  name?: string;
  words?: string[];
  wordsI18n?: { de?: string[]; br?: string[] };
}

export const ScrollingWordsRefSchema = `->{ name, words, wordsI18n }`;

export interface IBirthdayBannerTier {
  label?: string;
  labelI18n?: II18nText;
  discount?: string;
  highlighted?: boolean;
  planName?: string;
  planNameI18n?: II18nText;
  savingsText?: string;
  savingsTextI18n?: II18nText;
  badgeLabel?: string;
  badgeLabelI18n?: II18nText;
}

export interface IBirthdayBanner {
  isActive: boolean;
  countdownEndDate?: string;
  endsInLabel?: string;
  endsInLabelI18n?: II18nText;
  hoursLabel?: string;
  hoursLabelI18n?: II18nText;
  minutesLabel?: string;
  minutesLabelI18n?: II18nText;
  secondsLabel?: string;
  secondsLabelI18n?: II18nText;
  headerMainText?: string;
  headerMainTextI18n?: II18nText;
  popupMainText?: string;
  popupMainTextI18n?: II18nText;
  headerCtaLabel?: string;
  headerCtaLabelI18n?: II18nText;
  popupCtaLabel?: string;
  popupCtaLabelI18n?: II18nText;
  sectionLabel?: string;
  sectionLabelI18n?: II18nText;
  tiers?: IBirthdayBannerTier[];
  showTierBar?: boolean;
}

export const BirthdayBannerSchema = `{
  isActive,
  showTierBar,
  countdownEndDate,
  endsInLabel, endsInLabelI18n,
  hoursLabel, hoursLabelI18n,
  minutesLabel, minutesLabelI18n,
  secondsLabel, secondsLabelI18n,
  headerMainText, headerMainTextI18n,
  popupMainText, popupMainTextI18n,
  headerCtaLabel, headerCtaLabelI18n,
  popupCtaLabel, popupCtaLabelI18n,
  sectionLabel, sectionLabelI18n,
  tiers[]{ label, labelI18n, discount, highlighted, planName, planNameI18n, savingsText, savingsTextI18n, badgeLabel, badgeLabelI18n },
}`;

export interface ISummerSale {
  isActive: boolean;
  endDate?: string;
  ctaUrl?: string;
  bannerText?: string;
  bannerTextI18n?: II18nText;
  bannerCtaLabel?: string;
  bannerCtaLabelI18n?: II18nText;
  popupText?: string;
  popupTextI18n?: II18nText;
  popupSubtext?: string;
  popupSubtextI18n?: II18nText;
  popupCtaLabel?: string;
  popupCtaLabelI18n?: II18nText;
}

export const SummerSaleSchema = `{
  isActive,
  endDate,
  ctaUrl,
  bannerText, bannerTextI18n,
  bannerCtaLabel, bannerCtaLabelI18n,
  popupText, popupTextI18n,
  popupSubtext, popupSubtextI18n,
  popupCtaLabel, popupCtaLabelI18n,
}`;

// One scheduled banner pair: the singleton's own flash pair takes this shape
// too (lib/flashSale.ts `topLevelCampaign`), and the `campaigns` list holds
// the seasonal ones (Halloween, Black Month, ...), each with its own window.
export interface IFlashCampaign {
  _key?: string;
  name?: string;
  isEnabled?: boolean;
  startDate?: string;
  endDate?: string;
  showTopBanner?: boolean;
  topBanner?: ITopBanner;
  showPromoBanner?: boolean;
  promoBanner?: IPromoBanner;
}

export const FlashCampaignSchema = `{
  _key,
  name,
  isEnabled,
  startDate,
  endDate,
  showTopBanner,
  topBanner->${TopBannerSchema},
  showPromoBanner,
  promoBanner->${PromoBannerSchema},
}`;

// Global "Flash Sale" campaign singleton (id "flashSale"). Holds the flash
// banners plus their schedule as an overlay: while live, resolveFlashBanners
// in lib/flashSale.ts substitutes them for each page own banner settings;
// outside the window the page settings are used untouched. `campaigns` are
// further scheduled pairs, checked after the flash pair, in list order.
export interface IFlashSale {
  isEnabled?: boolean;
  name?: string;
  startDate?: string;
  endDate?: string;
  showDefaultTopBanner?: boolean;
  defaultTopBanner?: ITopBanner;
  showDefaultPromoBanner?: boolean;
  defaultPromoBanner?: IPromoBanner;
  showTopBanner?: boolean;
  topBanner?: ITopBanner;
  showPromoBanner?: boolean;
  promoBanner?: IPromoBanner;
  campaigns?: IFlashCampaign[];
}

export const FlashSaleSchema = `{
  isEnabled,
  name,
  startDate,
  endDate,
  showDefaultTopBanner,
  defaultTopBanner->${TopBannerSchema},
  showDefaultPromoBanner,
  defaultPromoBanner->${PromoBannerSchema},
  showTopBanner,
  topBanner->${TopBannerSchema},
  showPromoBanner,
  promoBanner->${PromoBannerSchema},
  campaigns[]${FlashCampaignSchema},
}`;

export interface ILaunchEvent {
  isEnabled?: boolean;
  name?: string;
  startDate?: string;
  endDate?: string;
  countdownEndDate?: string;
  endDateDe?: string;
  countdownEndDateDe?: string;
  ctaPathDe?: string;
  eyebrow?: string;
  eyebrowI18n?: II18nText;
  headline?: string;
  headlineI18n?: II18nText;
  ctaLabel?: string;
  ctaLabelI18n?: II18nText;
  ctaPath?: string;
  daysLabel?: string;
  daysLabelI18n?: II18nText;
  hoursLabel?: string;
  hoursLabelI18n?: II18nText;
  minutesLabel?: string;
  minutesLabelI18n?: II18nText;
  secondsLabel?: string;
  secondsLabelI18n?: II18nText;
}

export const LaunchEventSchema = `{
  isEnabled,
  name,
  startDate,
  endDate,
  countdownEndDate,
  endDateDe,
  countdownEndDateDe,
  ctaPathDe,
  eyebrow,
  eyebrowI18n,
  headline,
  headlineI18n,
  ctaLabel,
  ctaLabelI18n,
  ctaPath,
  daysLabel,
  daysLabelI18n,
  hoursLabel,
  hoursLabelI18n,
  minutesLabel,
  minutesLabelI18n,
  secondsLabel,
  secondsLabelI18n,
}`;

export interface ITradingCompetitionPopup {
  isEnabled?: boolean;
  name?: string;
  startDate?: string;
  endDate?: string;
  /** countdownEndDate, else the Trading Competition page's hero countdown date. */
  countdownTarget?: string;
  ctaPath?: string;
  eyebrow?: string;
  eyebrowI18n?: II18nText;
  headline?: string;
  headlineI18n?: II18nText;
  ctaLabel?: string;
  ctaLabelI18n?: II18nText;
  daysLabel?: string;
  daysLabelI18n?: II18nText;
  hoursLabel?: string;
  hoursLabelI18n?: II18nText;
  minutesLabel?: string;
  minutesLabelI18n?: II18nText;
  secondsLabel?: string;
  secondsLabelI18n?: II18nText;
}

export const TradingCompetitionPopupSchema = `{
  isEnabled,
  name,
  startDate,
  endDate,
  "countdownTarget": coalesce(
    countdownEndDate,
    *[_type == "pageTradingCompetition" && _id == "pageTradingCompetition"][0].countdownTarget
  ),
  ctaPath,
  eyebrow,
  eyebrowI18n,
  headline,
  headlineI18n,
  ctaLabel,
  ctaLabelI18n,
  daysLabel,
  daysLabelI18n,
  hoursLabel,
  hoursLabelI18n,
  minutesLabel,
  minutesLabelI18n,
  secondsLabel,
  secondsLabelI18n,
}`;

export interface IYoutubeLiveWidget {
  isEnabled?: boolean;
  channelLabel?: string;
  youtubeUrl?: string;
  youtubeChannelUrl?: string;
  liveChannel?: string;
}

export const YoutubeLiveWidgetSchema = `{
  isEnabled,
  channelLabel,
  youtubeUrl,
  youtubeChannelUrl,
  liveChannel,
}`;

export interface IAbTestVariant {
  _key?: string;
  variantKey?: string;
  page?: {
    _id?: string;
    _type?: string;
  };
}

export interface IAbTest {
  enabled?: boolean;
  posthogFlagKey?: string;
  variants?: IAbTestVariant[];
}

export const GROQAbTestSchema = `
  abTest {
    enabled,
    posthogFlagKey,
    variants[]{
      _key,
      variantKey,
      "page": page->{ _id, _type }
    }
  }
`;

// ── Careers ───────────────────────────────────────────────────────────────────

export interface ICareerCategory {
  _id?: string;
  name?: string;
  nameI18n?: II18nText;
  order?: number;
}

export const CareerCategorySchema = `{
  _id,
  name,
  nameI18n,
  order,
}`;

export interface ICareerBenefit {
  _key?: string;
  icon?: GROQImage;
  title?: string;
  description?: string;
}

export interface ICareerProcessStep {
  _key?: string;
  title?: string;
  description?: string;
  image?: GROQImage;
  imageScale?: number;
}

export interface ICareerSettings {
  whoIcon?: GROQImage;
  whoTitle?: PortableTextBlock[];
  whoText?: PortableTextBlock[];

  benefitsChip?: string;
  benefitsTitle?: PortableTextBlock[];
  benefitsCTA?: GROQButton;
  benefitsItems?: ICareerBenefit[];

  processChip?: string;
  processTitle?: PortableTextBlock[];
  processSteps?: ICareerProcessStep[];

  faqChip?: string;
  faqTitle?: PortableTextBlock[];
  faqItems?: IFaqItem[];

  finalCtaChip?: string;
  finalCtaTitle?: PortableTextBlock[];
  finalCtaButton?: GROQButton;
  finalCtaImage?: GROQImage;
}

export const CareerSettingsSchema = `{
  whoIcon${GROQImageSchema},
  whoTitle,
  whoText,

  benefitsChip,
  benefitsTitle,
  benefitsCTA${GROQButtonSchema},
  benefitsItems[]{
    _key,
    icon${GROQImageSchema},
    title,
    description,
  },

  processChip,
  processTitle,
  processSteps[]{
    _key,
    title,
    description,
    image${GROQImageSchema},
    imageScale,
  },

  faqChip,
  faqTitle,
  faqItems[]->${FaqItemSchema},

  finalCtaChip,
  finalCtaTitle,
  finalCtaButton${GROQButtonSchema},
  finalCtaImage${GROQImageSchema},
}`;

/** One open role, as projected for the careers overview listing. */
export interface ICareerRoleCard {
  _id?: string;
  listTitle?: string;
  listMeta?: string[];
  slug?: string;
  category?: ICareerCategory;
}

export const CareerRoleCardSchema = `{
  _id,
  listTitle,
  listMeta,
  "slug": settings.slug.current,
  category->${CareerCategorySchema},
}`;

export interface IBlogCategory {
  _id: string;
  name?: string;
  nameI18n?: II18nText;
  slug?: string;
  order?: number;
}

export const BlogCategorySchema = `{ _id, name, nameI18n, "slug": slug.current, order }`;

export const blogLivePostFilter = (scope: string, preview = false) => `
  _type == "pageBlogPost"
  && settings.language == ${scope}.settings.language
  && settings.domain == ${scope}.settings.domain${
    preview
      ? ''
      : `
  && defined(publishAt)
  && dateTime(publishAt) <= dateTime(now())`
  }
  && defined(settings.slug.current)
  && !(settings.slug.current match "/_variants/*")
`;

export interface IBlogImageHotspot {
  x: number;
  y: number;
  height?: number;
  width?: number;
}

export interface IBlogPostCard {
  _id: string;
  title?: string;
  excerpt?: string;
  publishAt?: string;
  slug?: string;
  mainImage?: Pick<GROQImage, 'src' | 'width' | 'height'>;
  mainImageAlt?: string;
  mainImageHotspot?: IBlogImageHotspot | null;
  category?: IBlogCategory | null;
}

export const BlogPostCardSchema = `{
  _id,
  title,
  "excerpt": coalesce(excerpt, settings.metaDescription),
  publishAt,
  "slug": settings.slug.current,
  mainImage{"src": asset->url, "width": asset->metadata.dimensions.width, "height": asset->metadata.dimensions.height},
  "mainImageAlt": mainImage.alt,
  "mainImageHotspot": mainImage.hotspot,
  category->${BlogCategorySchema},
}`;
